import {vnode} from 'snabbdom/build/package/vnode';
import {htmlDomApi} from 'snabbdom/build/package/htmldomapi';
import {NOW_EXCLUDED_FROM_HYDRATION_ATTR} from '@servicenow/ui-internal';

/**
 * This is a modified version of the snabbdom toVNode() to convert server-side rendered nodes to vnodes.
 * - it builds "sel" field only from the tagName of the element node,
 *   rather than a combination of selector, id, and class
 * - it assigns the class attribute value to the "className" property
 * - it skips the node if it has the NOW_EXCLUDED_FROM_HYDRATION_ATTR attribute
 */

export function toVNodeFromServerSideRenderedNode(node, domApi) {
	const api = domApi !== undefined ? domApi : htmlDomApi;
	let text;
	if (api.isElement(node)) {
		const sel = api.tagName(node).toLowerCase();
		const attrs = {};
		const children = [];
		let name;
		const elmAttrs = node.attributes;
		const elmChildren = node.childNodes;
		const props = {};
		for (let i = 0, n = elmAttrs.length; i < n; i++) {
			name = elmAttrs[i].nodeName;
			if (name === 'class') {
				props.className = elmAttrs[i].nodeValue;
				continue;
			}
			/**
			 * exclude the node from the vdom tree during hydration if it has non-falsy NOW_EXCLUDED_FROM_HYDRATION_ATTR attribute
			 * SSR may render nodes which are not managed by the virtual DOM, for example:
			 * <sn-canvas-screen>
			 *   #shadow-root
			 *   <screen-action-transformer-12345>
			 *       <macroponent-abcdef>
			 *       </macroponent-abcdef>
			 *   </screen-action-transformer-12345>
			 * </sn-canvas-screen>
			 * The screen action transformer and macroponent are not rendered by the view function of sn-canvas-screen.
			 * Instead, they are manually inserted with DOM manipulation. If we convert them into vnodes during hydration,
			 * they will be trashed in the snabbdom patch process because the new vdom tree does not contain them.
			 * To solve this issue, we add NOW_EXCLUDED_FROM_HYDRATION_ATTR attribute to the nodes that will
			 * be excluded from the vdom tree so snabbdom has no visibility of them and thus won't trash them.
			 */
			if (name === NOW_EXCLUDED_FROM_HYDRATION_ATTR && elmAttrs[i].nodeValue !== 'false') {
				return null;
			}
			attrs[name] = elmAttrs[i].nodeValue;
		}
		for (let i = 0, n = elmChildren.length; i < n; i++) {
			const childVNode = toVNodeFromServerSideRenderedNode(elmChildren[i], domApi);
			// skip the nullified child node if it has non-falsy NOW_EXCLUDED_FROM_HYDRATION_ATTR attribute
			if (childVNode) {
				children.push(childVNode);
			}
		}
		return vnode(sel, {attrs, props}, children, undefined, node);
	} else if (api.isText(node)) {
		text = api.getTextContent(node);
		return vnode(undefined, undefined, undefined, text, node);
	} else if (api.isComment(node)) {
		text = api.getTextContent(node);
		return vnode('!', {}, [], text, node);
	}
	return vnode('', {}, [], undefined, node);
}
